import axios from "axios";

import { API_ROOT } from ".";
import { exitDemoMode } from "../demo/session";
import type {
  AdminAccount,
  BusinessAccount,
  ClientAccount,
  EmployeeAccount,
  PaymentStatus,
  SocialAccount,
} from "../components/dashboard/utils/types";

const AUTH_API_URL = `${API_ROOT}/auth`;

export interface User {
  userId: string;
  firstName: string;
  secondName?: string;
  surname: string;
  email: string;
  isAdmin?: boolean;
  isSuperUser?: boolean;
}

export interface UserListItem extends User {
  name?: string;
  lastName?: string;
  role?: string;
  phoneNumber?: string;
  emailVerified?: boolean;
  createdAt?: string;
  hasCSASubscription?: boolean;
  subscriptionHistory?: SubscriptionHistoryEntry[];
}

export interface SubscriptionHistoryEntry {
  subscriptionTierId: string;
  subscriptionTierName: string;
  year: number;
}

export const signup = async (data: {
  firstName: string;
  secondName?: string;
  surname: string;
  email: string;
  password: string;
  confirmPassword: string;
  phoneNumber?: string;
  redirect?: string;
}) => {
  const res = await axios.post(`${AUTH_API_URL}/signup`, data);
  return res.data;
};

export const login = async (data: { email: string; password: string }) => {
  const res = await axios.post(`${AUTH_API_URL}/login`, data);
  return res.data;
};

export const resendVerificationEmail = async (data: {
  email: string;
  password: string;
}) => {
  const res = await axios.post(
    `${AUTH_API_URL}/resend-verification-email`,
    data,
  );
  return res.data as { message: string };
};

/** GET /auth/profile — business accounts omit firstName, secondName, surname. */
export interface Profile {
  email: string;
  phoneNumber?: string;
  role: string;
  hasCSASubscription?: boolean;
  subscriptionHistory?: SubscriptionHistoryEntry[];
  firstName?: string;
  secondName?: string;
  surname?: string;
  companyName?: string;
  companyRegistrationNumber?: string;
  address?: string;
  postalCode?: string;
  city?: string;
  shippingHeavyPrice?: number;
  shippingLightPrice?: number;
}

export function decodeTokenPayload(token: string): {
  userId?: string;
  role?: string;
} | null {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    return JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/"))) as {
      userId?: string;
      role?: string;
    };
  } catch {
    return null;
  }
}

export interface BusinessDetails {
  companyName?: string;
  companyRegistrationNumber?: string;
}

export async function getBusinessDetails(
  token: string,
): Promise<BusinessDetails | null> {
  try {
    const profile = await fetchProfile(token);
    if (
      profile.role === "business" ||
      profile.companyName != null ||
      profile.companyRegistrationNumber != null
    ) {
      return {
        companyName: profile.companyName,
        companyRegistrationNumber: profile.companyRegistrationNumber,
      };
    }
    return null;
  } catch {
    return null;
  }
}

export function getAuthUser(): {
  token: string;
  userId: string;
} | null {
  const token = localStorage.getItem("token");
  if (!token) return null;
  const decoded = decodeTokenPayload(token);
  if (!decoded?.userId) return null;
  return { token, userId: decoded.userId };
}

export function clearAuthStorage(): void {
  localStorage.removeItem("token");
  localStorage.removeItem("user");
  // Logging out of a demo session must also leave demo mode, or the next real
  // login attempt gets intercepted and refused.
  exitDemoMode();
}

export const fetchProfile = async (token: string): Promise<Profile> => {
  const res = await axios.get<Profile>(`${AUTH_API_URL}/profile`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

/** PATCH /auth/profile — only include fields allowed for the account role (see backend). */
export type UpdateProfilePayload = Partial<{
  firstName: string;
  secondName: string | null;
  surname: string;
  phoneNumber: string;
  companyName: string;
  companyRegistrationNumber: string;
  address: string;
  postalCode: string;
  city: string;
  shippingHeavyPrice: number | null;
  shippingLightPrice: number | null;
}>;

export async function updateProfile(
  token: string,
  payload: UpdateProfilePayload,
): Promise<Profile> {
  const res = await axios.patch<Profile>(`${AUTH_API_URL}/profile`, payload, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
  return res.data;
}

/** Permanently delete the logged-in user's account. */
export async function callDeleteOwnAccount(
  token: string,
): Promise<{ message: string }> {
  const res = await axios.delete<{ message: string }>(
    `${AUTH_API_URL}/profile`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  return res.data;
}

export const fetchUsers = async (token: string): Promise<UserListItem[]> => {
  const res = await axios.get<UserListItem[]>(`${AUTH_API_URL}/users`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

export type CreateUserPayload =
  | Omit<AdminAccount, "password">
  | Omit<BusinessAccount, "password">
  | Omit<EmployeeAccount, "password">
  | Omit<ClientAccount, "password">
  | Omit<SocialAccount, "password">;

export const callCreateUser = async (
  token: string,
  payload: CreateUserPayload,
): Promise<{ message: string; userId: string }> => {
  const res = await axios.post<{ message: string; userId: string }>(
    `${AUTH_API_URL}/users`,
    payload,
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );
  return res.data;
};

export const verifyEmail = async (token: string) => {
  const res = await axios.get(`${AUTH_API_URL}/verify-email`, {
    params: { token },
  });
  return res.data;
};

export const forgotPassword = async (data: { email: string }) => {
  const res = await axios.post(`${AUTH_API_URL}/forgot-password`, data);
  return res.data;
};

export const resetPassword = async (data: {
  token: string;
  password: string;
  confirmPassword: string;
}) => {
  const res = await axios.post(`${AUTH_API_URL}/reset-password`, data);
  return res.data;
};

export type AssignSubscriptionPayload = {
  subscriptionTierId: string;
  pickupLocationId: string;
  pickupDay: string;
  subscriptionPaymentStatus?: PaymentStatus;
  friendPickupFirstName?: string | null;
  friendPickupLastName?: string | null;
  friendPickupPhone?: string | null;
};

/** Admin: assign a CSA pickup to a user without one. */
export async function callAssignSubscription(
  token: string,
  userId: string,
  payload: AssignSubscriptionPayload,
): Promise<{ message: string; hasCSASubscription: true }> {
  const res = await axios.post<{ message: string; hasCSASubscription: true }>(
    `${AUTH_API_URL}/users/${encodeURIComponent(userId)}/assign-subscription`,
    payload,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  return res.data;
}

/** Admin: send CSA payment reminder email (checkout created when member clicks pay). */
export async function callSendCsaPaymentReminder(
  token: string,
  userId: string,
): Promise<{
  message: string;
  emailSent?: boolean;
}> {
  const res = await axios.post<{
    message: string;
    emailSent?: boolean;
  }>(
    `${AUTH_API_URL}/users/${encodeURIComponent(userId)}/send-csa-payment-reminder`,
    {},
    { headers: { Authorization: `Bearer ${token}` } },
  );
  return res.data;
}

/** Admin: remove a user's CSA pickup(s). Role is unchanged. */
export async function callRemoveSubscription(
  token: string,
  userId: string,
): Promise<{ message: string; hasCSASubscription: false }> {
  const res = await axios.post<{ message: string; hasCSASubscription: false }>(
    `${AUTH_API_URL}/users/${encodeURIComponent(userId)}/remove-subscription`,
    {},
    { headers: { Authorization: `Bearer ${token}` } },
  );
  return res.data;
}

/** Admin: permanently delete a user account from the database. */
export async function callDeleteUser(
  token: string,
  userId: string,
): Promise<{ message: string }> {
  const res = await axios.delete<{ message: string }>(
    `${AUTH_API_URL}/users/${encodeURIComponent(userId)}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  return res.data;
}
